import { Component, ElementRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { Api } from 'src/app/services/api';
import { Globals } from 'src/app/services/globals';
import { availableExaktaSKUs, frameDimensions } from '../../../../../../../common/common-third-party/exakta';
import { allCanvasSizes, allPosterSizes } from '../../../../../../../common/common-types/poster-size';
import { countryCodes } from '../../../../../../../common/common-types/country';
import { getCurrencyByCountry } from '../../../../../../../common/common-helpers/currency';
import { availableSKUTransformers } from '../../../../../../../common/common-helpers/sku-transformers';
import { TranslationService } from '../../../services/translation';
import _ from 'lodash';
import { IStoreSettings } from '../../../../../../../common/common-interfaces/store-settings';
import { IStoreCloneOptions, IStoreCloneResult } from '../../../../../../../common/common-interfaces/store-clone';

@Component({
    selector: 'store-settings',
    templateUrl: './store-settings.component.html',
    styleUrls: ['./store-settings.component.scss'],
    standalone: false
})
export class StoreSettingsComponent {
    pictufyCollections = '';
    pictufyLists = '';
    creditsToAdd = 0;
    availableExaktaSKUs = availableExaktaSKUs;
    availableCanvasSizes = allCanvasSizes;
    availablePosterSizes = allPosterSizes;
    frameColors = [];
    getCurrencyByCountry = getCurrencyByCountry;
    availableSKUTransformers = availableSKUTransformers;
    testEmailAddress = '';

    /** Working copy of the store, shown prefilled in the "Klona butik" tab. */
    cloneSettings: IStoreSettings;
    cloneOptions: IStoreCloneOptions = {
        prices: true,
        pictufySettings: false,
        initFtp: true
    };
    /** Keeps the Exakta customer name and FTP root folder following the name field. */
    syncCloneNames = true;
    cloning = false;
    cloneError = '';
    clonedStore: IStoreSettings;
    /** Hides the shared footer buttons, which would save the source store. */
    cloneTabActive = false;
    constructor(private api: Api, private router: Router, private route: ActivatedRoute, public globals: Globals, private sanitizer: DomSanitizer, private translations: TranslationService) {

    }

    async ngOnInit() {
        this.globals.storeSettings = await this.api.store().get<any>(`/settings`);
        if (!this.globals.storeSettings.creditPrices) {
            this.globals.storeSettings.creditPrices = {}
        }

        if (!this.globals.storeSettings.enabledFeatures) {
            this.globals.storeSettings.enabledFeatures = {};
        }

        if (!this.globals.storeSettings.exaktaSettings) {
            this.globals.storeSettings.exaktaSettings = {} as any;
        }

        if (!this.globals.storeSettings.shopifySettings) {
            this.globals.storeSettings.shopifySettings = {} as any;
        }

        if (!this.globals.storeSettings.woocommerceSettings) {
            this.globals.storeSettings.woocommerceSettings = {} as any;
        }

        if (!this.globals.storeSettings.googleStorageSettings) {
            this.globals.storeSettings.googleStorageSettings = { bucketName: '' };
        }

        if (!this.globals.storeSettings.klarnaSettings) {
            this.globals.storeSettings.klarnaSettings = {} as any;
        }

        if (!this.globals.storeSettings.fyndiqSettings) {
            this.globals.storeSettings.fyndiqSettings = {} as any;
        }
        if (!this.globals.storeSettings.emailSettings) {
            this.globals.storeSettings.emailSettings = {
                sender: {
                    name: '',
                    address: ''
                },
                headerImageUrl: '',
            };
        }

        if (!this.globals.storeSettings.countrySpecificShippingPrices) {
            this.globals.storeSettings.countrySpecificShippingPrices = {};
        }

        if (!this.globals.storeSettings.newOrderFreezeTimeMinutes) {
            this.globals.storeSettings.newOrderFreezeTimeMinutes = 0;
        }

        if (!this.globals.storeSettings.texPrintSettings) {
            this.globals.storeSettings.texPrintSettings = {
                brand: 'ACE',
                shippingMethod: 'regular'
            }
        }

        for (let sku of this.availableExaktaSKUs) {
            if (!this.globals.storeSettings.creditPrices[sku]) {
                this.globals.storeSettings.creditPrices[sku] = {
                    printPrice: 0,
                    additionalPrice: 0,
                    shippingPrice: 0
                }
            }

            if (!this.globals.storeSettings.defaultSizePricesByCountry)
                this.globals.storeSettings.defaultSizePricesByCountry = {};

            for (let country of countryCodes) {
                if (country === 'se') continue;

                if (!this.globals.storeSettings.countrySpecificShippingPrices[country])
                    this.globals.storeSettings.countrySpecificShippingPrices[country] = {};
                if (!this.globals.storeSettings.countrySpecificShippingPrices[country][sku])
                    this.globals.storeSettings.countrySpecificShippingPrices[country][sku] = { shippingPrice: 0 };

                if (!this.globals.storeSettings.defaultSizePricesByCountry[country])
                    this.globals.storeSettings.defaultSizePricesByCountry[country] = {
                        poster: {},
                        canvas: {},
                        frames: {},
                        wallpaper: { squareMeterPrice: 0 }
                    }
                if (!this.globals.storeSettings.defaultSizePricesByCountry[country].canvas)
                    this.globals.storeSettings.defaultSizePricesByCountry[country].canvas = {};
                if (!this.globals.storeSettings.defaultSizePricesByCountry[country].frames)
                    this.globals.storeSettings.defaultSizePricesByCountry[country].frames = {};
                if (!this.globals.storeSettings.defaultSizePricesByCountry[country].wallpaper)
                    this.globals.storeSettings.defaultSizePricesByCountry[country].wallpaper = { squareMeterPrice: 0 };
                if (!this.globals.storeSettings.defaultSizePricesByCountry[country].poster)
                    this.globals.storeSettings.defaultSizePricesByCountry[country].poster = {};
            }


        }


        if (!this.globals.storeSettings.defaultSizePrices)
            this.globals.storeSettings.defaultSizePrices = {
                poster: {},
                canvas: {},
                frames: {},
                wallpaper: { squareMeterPrice: 0 }
            }

        if (!this.globals.storeSettings.defaultSizePrices.wallpaper) {
            this.globals.storeSettings.defaultSizePrices.wallpaper = { squareMeterPrice: 0 };
        }

        if (!this.globals.storeSettings.defaultSizePrices.canvas)
            this.globals.storeSettings.defaultSizePrices.canvas = {};

        if (!this.globals.storeSettings.defaultSizePrices.frames)
            this.globals.storeSettings.defaultSizePrices.frames = {};

        for (let sku of this.availableExaktaSKUs) {
            if (sku.startsWith('FRAME_')) {
                let color = sku.split('_').slice(2).join('_');
                let size = sku.split('_')[1];

                let frameSize = frameDimensions({ width: size.split('x')[0], height: size.split('x')[1] });

                if (!this.frameColors.includes(color))
                    this.frameColors.push(color);

                if (!this.globals.storeSettings.defaultSizePrices.frames[frameSize])
                    this.globals.storeSettings.defaultSizePrices.frames[frameSize] = {};
                if (!this.globals.storeSettings.defaultSizePrices.frames[frameSize][color])
                    this.globals.storeSettings.defaultSizePrices.frames[frameSize][color] = 0;

                for (let country of countryCodes) {
                    if (this.globals.storeSettings.defaultSizePricesByCountry[country]) {
                        if (!this.globals.storeSettings.defaultSizePricesByCountry[country].frames[frameSize])
                            this.globals.storeSettings.defaultSizePricesByCountry[country].frames[frameSize] = {};

                        if (!this.globals.storeSettings.defaultSizePricesByCountry[country].frames[frameSize][color])
                            this.globals.storeSettings.defaultSizePricesByCountry[country].frames[frameSize][color] = 0;
                    }
                }

            }
        }



        if (!this.globals.storeSettings.pictufySettings)
            this.globals.storeSettings.pictufySettings = {
                enabled: false,
                grade: 4,
                productsInBatch: 1000,
                canvasSizesToCreate: {},
                posterSizesToCreate: {},
            }

        if (!this.globals.storeSettings.pictufySettings.canvasSizesToCreate)
            this.globals.storeSettings.pictufySettings.canvasSizesToCreate = {};

        if (!this.globals.storeSettings.pictufySettings.posterSizesToCreate)
            this.globals.storeSettings.pictufySettings.posterSizesToCreate = {};


        if (!this.globals.storeSettings.pictufySettings.artworkTypes)
            this.globals.storeSettings.pictufySettings.artworkTypes = [];

        if (!this.globals.storeSettings.pictufySettings.geometries)
            this.globals.storeSettings.pictufySettings.geometries = [];

        if (!this.globals.storeSettings.pictufySettings.collections)
            this.globals.storeSettings.pictufySettings.collections = [];

        if (!this.globals.storeSettings.pictufySettings.tagCategories)
            this.globals.storeSettings.pictufySettings.tagCategories = [];

        if (!this.globals.storeSettings.pictufySettings.lists)
            this.globals.storeSettings.pictufySettings.lists = [];

        this.ensurePictufyMockupFilter();


        if (!this.globals.storeSettings.printFileImport)
            this.globals.storeSettings.printFileImport = {
                defaultDPI: 72,
                defaultBleedMm: 4,
                importAs: 'PRINT_FILE'
            };

        this.pictufyCollections = this.globals.storeSettings.pictufySettings.collections.join(',');
        this.pictufyLists = this.globals.storeSettings.pictufySettings.lists.join(',');

        this.resetCloneForm();
    }

    /**
     * Brings the Pictufy mockup filter up to the shape the template binds to.
     * Stores saved before per-list mockup ids existed have no listSpecificIds at
     * all, and an empty list field used to be stored as a single blank entry -
     * both used to throw as soon as the Pictufy tab rendered.
     */
    private ensurePictufyMockupFilter() {
        let pictufySettings = this.globals.storeSettings.pictufySettings;

        if (!pictufySettings.mockupImageFilter)
            pictufySettings.mockupImageFilter = {
                enabled: false,
                ids: [],
                listSpecificIds: {}
            };

        if (!pictufySettings.mockupImageFilter.ids)
            pictufySettings.mockupImageFilter.ids = [];

        if (!pictufySettings.mockupImageFilter.listSpecificIds)
            pictufySettings.mockupImageFilter.listSpecificIds = {};

        pictufySettings.lists = this.withoutBlankEntries(pictufySettings.lists);
        pictufySettings.collections = this.withoutBlankEntries(pictufySettings.collections);

        for (let listId of pictufySettings.lists) {
            if (!pictufySettings.mockupImageFilter.listSpecificIds[listId])
                pictufySettings.mockupImageFilter.listSpecificIds[listId] = [];
        }
    }

    /** A blank id is not a usable key, and an empty input splits into one. */
    private withoutBlankEntries(entries: string[]): string[] {
        return (entries || []).map(entry => (entry || '').trim()).filter(entry => entry.length > 0);
    }

    /**
     * Prefills the clone form with this store's settings. The fields that have
     * to be unique per store are suggested or blanked instead of copied, so a
     * clone can never take over the source store's access code, custom domain
     * or Shopify connection. A clone also starts disabled, to keep it from
     * processing orders before it has been reviewed.
     */
    resetCloneForm() {
        let clone: IStoreSettings = _.cloneDeep(this.globals.storeSettings);

        delete clone._id;
        clone.name = `${this.globals.storeSettings.name}-copy`;
        clone.accessCode = '';
        clone.customOrderGatewayDomain = '';
        clone.disabled = true;
        clone.credits = 0;
        clone.shopifySettings = Object.assign({}, clone.shopifySettings, {
            domain: '',
            shopName: '',
            accessToken: ''
        });

        if (!clone.exaktaSettings)
            clone.exaktaSettings = {} as any;

        if (!clone.exaktaSettings.ftpSettings)
            clone.exaktaSettings.ftpSettings = { host: '', user: '', password: '', rootFolder: '' };

        this.cloneSettings = clone;
        this.syncCloneNames = true;
        this.cloneError = '';
        this.clonedStore = null;
        this.cloneNameChanged();
    }

    /** Mirrors the store name into the Exakta customer name and FTP root folder. */
    cloneNameChanged() {
        if (!this.syncCloneNames)
            return;

        this.cloneSettings.exaktaSettings.customerName = this.cloneSettings.name;
        this.cloneSettings.exaktaSettings.ftpSettings.rootFolder = this.cloneSettings.name;
    }

    async cloneStore() {
        if (!confirm(this.translations.translate('Är du säker på att du vill skapa en ny butik med de här inställningarna?')))
            return;

        this.cloning = true;
        this.cloneError = '';
        this.clonedStore = null;

        try {
            let result = await this.api.store().post<IStoreCloneResult>('/settings/clone', {
                settings: this.cloneSettings,
                options: this.cloneOptions
            });

            this.clonedStore = result.store;
        }
        catch (e) {
            console.error(e);
            this.cloneError = (e && e.error && e.error.message) || this.translations.translate('Något gick fel.');
        }
        finally {
            this.cloning = false;
        }
    }

    /** Reloads the panel on the new store, so every component picks it up. */
    openClonedStore() {
        window.location.href = `/store/${this.clonedStore._id}/store-settings`;
    }

    removePictufyMockupId(id): void {
        console.log(`Removing ${id}`);
        const ids = this.globals.storeSettings.pictufySettings.mockupImageFilter.ids;

        if (!ids)
            return;

        const index = ids.indexOf(id);

        if (index >= 0) {
            ids.splice(index, 1);
        }
    }

    addPictufyMockupId(event): void {
        const input = event.input;
        const value = event.value;

        if ((value || '').trim()) {
            if (!this.globals.storeSettings.pictufySettings.mockupImageFilter.ids)
                this.globals.storeSettings.pictufySettings.mockupImageFilter.ids = [];

            this.globals.storeSettings.pictufySettings.mockupImageFilter.ids.push(value);
        }

        if (input) {
            input.value = '';
        }
    }

    removePictufyListMockupId(list, id): void {
        console.log(`Removing ${id}`);
        const ids = this.pictufyListMockupIds(list);

        if (!ids)
            return;

        const index = ids.indexOf(id);

        if (index >= 0) {
            ids.splice(index, 1);
        }
    }

    /** The mockup ids stored for one Pictufy list, or null if there are none. */
    pictufyListMockupIds(list): string[] {
        let mockupImageFilter = this.globals.storeSettings.pictufySettings.mockupImageFilter;

        if (!mockupImageFilter || !mockupImageFilter.listSpecificIds)
            return null;

        return mockupImageFilter.listSpecificIds[list];
    }

    addPictufyListMockupId(list, event): void {
        const input = event.input;
        const value = event.value;

        if ((value || '').trim()) {
            this.ensurePictufyMockupFilter();

            if (!this.globals.storeSettings.pictufySettings.mockupImageFilter.listSpecificIds[list])
                this.globals.storeSettings.pictufySettings.mockupImageFilter.listSpecificIds[list] = [];

            this.globals.storeSettings.pictufySettings.mockupImageFilter.listSpecificIds[list].push(value);
        }

        if (input) {
            input.value = '';
        }
    }


    async addTagCategory() {
        this.globals.storeSettings.pictufySettings.tagCategories.push({
            name: '',
            tags: ''
        })
    }

    async removeTagCategory(tagCategory) {
        this.globals.storeSettings.pictufySettings.tagCategories.splice(this.globals.storeSettings.pictufySettings.tagCategories.indexOf(tagCategory), 1);
    }

    async save() {
        this.globals.storeSettings.pictufySettings.collections = this.withoutBlankEntries(this.pictufyCollections.split(','));
        this.globals.storeSettings.pictufySettings.lists = this.withoutBlankEntries(this.pictufyLists.split(','));

        this.ensurePictufyMockupFilter();

        // Switch SKU to landscape and set the same price
        for (let sku of this.availableExaktaSKUs) {
            let split = [];

            // Poster
            if (sku.includes('Poster_') && (sku.includes('_obestruket') || sku.includes('_bestruket'))) {
                split = sku.split('_');
            }

            if (split.length) {
                let sizeSplit = split[1].split('x');
                this.globals.storeSettings.creditPrices[`${split[0]}_${sizeSplit[1]}x${sizeSplit[0]}_${split[2]}`] = this.globals.storeSettings.creditPrices[sku];
            }

            split = [];

            // Canvas
            if (sku.includes('Canvas_')) {
                split = sku.split('_');
            }

            if (split.length) {
                let sizeSplit = split[1].split('x');
                this.globals.storeSettings.creditPrices[`${split[0]}_${sizeSplit[1]}x${sizeSplit[0]}`] = this.globals.storeSettings.creditPrices[sku];
            }

        }

        await this.api.store().patch<any>(`/settings`, this.globals.storeSettings);
    }


    async addCredits() {
        let result = await this.api.adminStore().post<any>(`/add-credits`, { amount: this.creditsToAdd });
        this.creditsToAdd = 0;
        if (result.succeeded)
            this.globals.storeSettings.credits = result.currentCredits
    }


    async testDispatchEmail() {
        await this.api.store().post<any>(`/settings/test-dispatch-email`, {
            email: this.testEmailAddress
        });
    }

    async importPrices() {
        if (!confirm(this.translations.translate('Är du säker på att du vill importera priser från google sheet för den här butiken?'))) return;
        try {
            await this.api.store().post<any>('/settings/import-prices', {});
            await this.ngOnInit();
            alert(this.translations.translate('Priser importerades!'));
        } catch (e) {
            console.error(e);
            alert(this.translations.translate('Något gick fel.'));
        }
    }

    ngOnDestroy(): void {


    }

}


